import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const nameKey = (n) => (n || '').normalize('NFKC').toLowerCase().split(/\s+/).filter(Boolean).join(' ');

const CustomerReviewsSection = () => {
  const [googleReviews, setGoogleReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reviews/google`).then((r) => setGoogleReviews(r.data)).catch(() => {});
  }, []);

  const manualReviews = [
    {
      id: 'review-carlie-grainger',
      name: 'Carlie Grainger',
      company: 'Airbnb host',
      rating: 5,
      review: 'I have been using NeatWave for my Airbnb cleaning while I am away and not able to clean myself. I have been in touch with Jefferson for organization and Elvis for the cleaning, plus other team members. I started with a trial clean which I was very happy with, and each clean since has been great. The team provide photos and videos for peace of mind after all cleans, and they were able to change the timings of cleans last minute due to issues with the keys from my side, which was really appreciated. Most importantly, my guests have all noted that the apartment has been spotless after a NeatWave clean. Thank you to the team, I will continue using your services in the future!',
      verified: true,
      platform: 'Google',
      url: 'https://share.google/uBeud5eS1zAftqceF'
    },
    {
      id: 'review-ash-heath',
      name: 'Ash Heath',
      rating: 5,
      review: 'I booked a move-in deep clean. The house had a lot of dirt and grime built up from over the years. Jefferson from Neatwave did a fantastic job of cleaning it and left the place spotless. Very happy with the service. Thank you!',
      verified: true,
      platform: 'Google',
      url: 'https://share.google/WDS4L26Pa22XUXU22'
    },
    {
      id: 'review-charlie-redding',
      name: 'Charlie Redding',
      company: 'Pinnacle Property Management',
      rating: 5,
      review: 'We had Neatwave carry out all of our end of tenancy cleans when our student tenancies swapped over, they completed around 60 oven cleans for us and they were fab, very professional and reasonably priced, would definitely recommend.',
      verified: true,
      platform: 'Google',
      url: 'https://share.google/Dq7jzl0rfJbYbCovR'
    },
    {
      id: 'review-elvis-madu',
      name: 'Elvis Madu',
      rating: 5,
      review: 'I was highly satisfied with the cleaning services provided by Neatwave Cleaning Services. The service was excellent, professional, and reliable, with great attention to detail. I thoroughly enjoyed the experience and would confidently recommend Neatwave Cleaning Services to anyone looking for quality cleaning services.',
      verified: true,
      platform: 'Google',
      url: 'https://share.google/PESUmj8IOrAvusGG1'
    },
    {
      id: 'review-1',
      name: 'David Matthews',
      rating: 5,
      review: 'An outstanding cleaning service, very polite, and an absolute pleasure to deal with. 5 stars.',
      verified: true,
      platform: 'Google'
    },
    {
      id: 'review-2',
      name: 'Superior Shania',
      rating: 5,
      review: 'Excellent and prompt service I received from this team. Satisfied client 💃💃',
      verified: true,
      platform: 'Google'
    },
    {
      id: 'review-3',
      name: 'Michelle Tsang',
      rating: 5,
      review: 'Professional cleaners who did a fantastic job of deep cleaning my parents house. We now use them...',
      verified: true,
      platform: 'Google'
    },
    {
      id: 'review-4',
      name: 'John Olugbenga',
      rating: 4,
      review: 'Excellent cleaning service. I recommend them for anyone.',
      verified: true,
      platform: 'Google'
    },
    {
      id: 'review-5',
      name: 'Wyn Williams',
      rating: 5,
      review: 'Great cleaning service, very reliable and high quality work.',
      verified: true,
      platform: 'Google'
    }
  ];

  const googleCards = googleReviews.map((g) => ({
    id: `google-${g.id}`,
    name: g.name,
    rating: g.rating,
    review: g.review,
    verified: true,
    platform: 'Google',
  }));
  const googleNames = new Set(googleCards.map((g) => nameKey(g.name)));
  const reviews = [...googleCards, ...manualReviews.filter((m) => !googleNames.has(nameKey(m.name)))];

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, index) => (
          <Star
            key={`star-${index}`}
            className={`w-5 h-5 ${
              index < rating 
                ? 'fill-amber-400 text-amber-400' 
                : 'fill-slate-200 text-slate-200'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <section id="reviews" className="scroll-mt-32 py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Customer Reviews
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Real feedback from our customers across Cardiff
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review) => (
            <div
              key={review.id}
              data-testid={`review-card-${review.id}`}
              className="bg-white rounded-2xl border-2 border-slate-100 p-6 hover:border-sky-300 hover:shadow-xl transition-all duration-300"
            >
              {/* Header with Name and Verified Badge */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-base">
                        {review.name}
                      </h3>
                      {review.company && (
                        <div className="text-xs text-slate-500">{review.company}</div>
                      )}
                      {review.verified && (
                        <div className="flex items-center gap-1 text-xs text-green-600">
                          <CheckCircle className="w-3 h-3" />
                          <span>Verified {review.platform} Review</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Google Logo Badge */}
                <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-lg border border-slate-200">
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="text-xs font-medium text-slate-600">Google</span>
                </div>
              </div>

              {/* Star Rating */}
              <div className="mb-3">
                {renderStars(review.rating)}
              </div>

              {/* Review Text */}
              <p className="text-slate-700 leading-relaxed text-sm">
                "{review.review}"
              </p>
              {review.url && (
                <a href={review.url} target="_blank" rel="noopener noreferrer" data-testid={`review-link-${review.id}`} className="mt-3 inline-block text-xs font-semibold text-sky-700 hover:underline">
                  View on Google
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Google Reviews CTA */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-slate-50 rounded-full border border-slate-200">
            <div className="flex -space-x-2">
              {[...Array(5)].map((_, i) => (
                <Star key={`avg-star-${i}`} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="text-sm font-semibold text-slate-900" data-testid="reviews-average">4.9 average on Google</span>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://g.page/r/CUljXn3XepPfEBI/review"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-leave-google-btn"
              className="inline-flex items-center gap-2 rounded-full border-2 border-sky-600 px-6 py-3 text-sm font-bold text-sky-700 transition-colors duration-200 hover:bg-sky-50"
            >
              Leave us a review
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerReviewsSection;
